import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useEnabledQuery } from '../../../ui/hooks/useEnabledQuery';
import { ResourceType } from '../../enumerations/ResourceType';
import { post } from '../APIVersion1';
import { PersistableResource, PersistableSchemaType } from '../persistableResourceTypes';
import { useMutationWithDelay } from '../useMutationWithDelay';
import { useResourceAdapterActions } from './useResourceAdapterActions';

export function useMultiParentlessResourceAdapterActions<R extends PersistableResource>(
    apiPostfix: string,
    resourceType: ResourceType,
    resourceSchema: PersistableSchemaType<R>
) {
    const actions = useResourceAdapterActions(apiPostfix, resourceType, resourceSchema);
    const getEnabled = useEnabledQuery();

    const useAddResource = () => {
        return useMutationWithDelay(
            (mutateObject: R) =>
                post(`${apiPostfix}`, JSON.stringify(mutateObject)).then((result) =>
                    actions.processMutationResponse(result, 'ADD')
                ),
            z.unknown()
        );
    };

    const useGetAllResources = () => {
        return useQuery({
            queryKey: [resourceType],
            queryFn: () => actions.getAllResources(),
            enabled: getEnabled(true),
        });
    };

    return {
        useValidateResource: actions.useValidateResource,
        useGetResource: actions.useGetResource,
        useUpdateResource: actions.useUpdateResource,
        useDeleteResource: actions.useDeleteResource,
        processMutationResponse: actions.processMutationResponse,
        processGetResponse: actions.processGetResponse,
        processGetCollectionResponse: actions.processGetCollectionResponse,
        processGetResourceResponse: actions.processGetResourceResponse,
        useGetAllResources,
        useAddResource,
    };
}
